import { MindbodyContract } from 'types/contract';

export interface StoredCardExp {
  /** Année 4 chiffres ex 2027. */
  ExpYear: number | string;
  /** Mois 1-12. */
  ExpMonth: number | string;
}

export interface CardEngagementCheck {
  /** True si la carte couvre toute la période d'engagement minimum. */
  valid: boolean;
  /** Date estimée de fin d'engagement (today + N * frequencyUnit). */
  endEngagement: Date;
  /** Date d'expiration effective de la carte (dernier jour du mois indiqué). */
  cardExpiresAt: Date;
}

/**
 * Calcule la date de fin d'engagement minimum pour un contract.
 * Utilise `NumberOfAutopays` × `AutopaySchedule.FrequencyTimeUnit` à compter
 * de `startDate`. Renvoie null si on n'a pas assez d'info pour calculer.
 */
export function computeEngagementEndDate(contract: MindbodyContract, startDate: Date = new Date()): Date | null {
  const n = contract.NumberOfAutopays;
  const unit = contract.AutopaySchedule?.FrequencyTimeUnit;
  if (!n || n <= 0 || !unit) return null;

  const end = new Date(startDate);
  switch (unit) {
    case 'Daily':
      end.setDate(end.getDate() + n);
      break;
    case 'Weekly':
      end.setDate(end.getDate() + n * 7);
      break;
    case 'Monthly':
      end.setMonth(end.getMonth() + n);
      break;
    case 'Yearly':
      end.setFullYear(end.getFullYear() + n);
      break;
    default:
      return null;
  }
  return end;
}

/**
 * Vérifie qu'une carte stockée reste valide jusqu'à la fin de l'engagement
 * du contract. Retourne `valid=true` si OK, `valid=false` sinon. Dans tous
 * les cas, fournit les dates calculées pour pouvoir construire un message
 * UX explicite.
 *
 * Une carte expire au dernier jour du mois indiqué (convention bancaire).
 */
export function isCardValidThroughContractEngagement(
  card: StoredCardExp,
  contract: MindbodyContract,
  startDate: Date = new Date(),
): CardEngagementCheck | null {
  const endEngagement = computeEngagementEndDate(contract, startDate);
  if (!endEngagement) return null;

  const expYear = typeof card.ExpYear === 'string' ? parseInt(card.ExpYear, 10) : card.ExpYear;
  const expMonth = typeof card.ExpMonth === 'string' ? parseInt(card.ExpMonth, 10) : card.ExpMonth;
  if (!expYear || !expMonth || Number.isNaN(expYear) || Number.isNaN(expMonth)) return null;

  // Dernier jour du mois d'expiration (jour 0 du mois suivant)
  const cardExpiresAt = new Date(expYear, expMonth, 0, 23, 59, 59, 999);

  return {
    valid: cardExpiresAt >= endEngagement,
    endEngagement,
    cardExpiresAt,
  };
}

/**
 * Format français court pour afficher une date dans un message UX.
 * Ex: "septembre 2026".
 */
export function formatMonthYearFR(d: Date): string {
  return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
}
